import { Controller } from "@hotwired/stimulus"
import { Dropdown } from "bootstrap"

/**
 * Dropdown Close Controller
 *
 * Handles closing mobile dropdown menus when close button is clicked
 *
 * Usage:
 * - Add `data-controller="dropdown-close"` to dropdown close button
 * - Add `data-dropdown-close-target="button"` to the close button
 */
export default class extends Controller {
  static targets = ["button"]

  connect() {
    if (this.hasButtonTarget) {
      this.buttonTarget.addEventListener('click', this.closeDropdown.bind(this))
    }
  }

  disconnect() {
    if (this.hasButtonTarget) {
      this.buttonTarget.removeEventListener('click', this.closeDropdown.bind(this))
    }
  }

  closeDropdown(event) {
    event.preventDefault()
    event.stopPropagation()
    
    // Find the dropdown menu that contains this close button
    const dropdownMenu = this.element.closest('.dropdown-menu')
    if (dropdownMenu) {
      // Check if this is the X button (immediate close) or back link (slide out)
      const isCloseButton = this.element.classList.contains('btn-close')
      
      if (isCloseButton) {
        // X button: close immediately
        this.closeImmediately(dropdownMenu)
      } else {
        // Back link: slide out with animation
        this.slideOut(dropdownMenu)
      }
    }
  }

  closeImmediately(dropdownMenu) {
    // Remove show class immediately
    dropdownMenu.classList.remove('show')
    
    // Also hide via Bootstrap's dropdown API if available
    const dropdownToggle = this.findDropdownToggle(dropdownMenu)
    if (dropdownToggle) {
      const dropdown = Dropdown.getInstance(dropdownToggle)
      if (dropdown) {
        dropdown.hide()
      }
    }
  }

  slideOut(dropdownMenu) {
    // Keep the show class during animation
    // Set the transition and start slide-out
    dropdownMenu.style.transition = 'right 0.2s ease'
    dropdownMenu.style.right = '-100vw'
    
    // Wait for animation to complete, then remove show class
    setTimeout(() => {
      // Now remove the show class and clean up
      dropdownMenu.classList.remove('show')
      dropdownMenu.style.right = ''
      dropdownMenu.style.transition = ''
      
      // Also hide via Bootstrap's dropdown API if available
      const dropdownToggle = this.findDropdownToggle(dropdownMenu)
      if (dropdownToggle) {
        const dropdown = Dropdown.getInstance(dropdownToggle)
        if (dropdown) {
          dropdown.hide()
        }
      }
    }, 200) // Match the CSS transition duration
  }

  findDropdownToggle(dropdownMenu) {
    // Look for the dropdown toggle that controls this menu
    const menuId = dropdownMenu.getAttribute('aria-labelledby')
    if (menuId) {
      return document.getElementById(menuId)
    }
    
    // Fallback: find the closest dropdown toggle
    const dropdownContainer = dropdownMenu.closest('[data-controller*="dropdown-hover"]')
    if (dropdownContainer) {
      return dropdownContainer.querySelector('[data-dropdown-hover-target="toggle"]')
    }
    
    return null
  }
}
